import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Plane, MapPin, MapPinned, TriangleAlert } from "lucide-react";

import { BiDirectionalArrowIcon } from "../../assets/icons/bi-directional-arrow";

import { Button } from "../ui/button";
import { cn } from "../../utils/lib";

import { setModeChange } from "../../redux/pricing/slice";

import { MODES } from "../../constants/booking-modes";
import { EXTRANET_PRICING_MODES } from "../../constants/extranet-pricing-modes";

// // This Constant is only this component specific
const PRICING_MODES_CONFIG = [
  {
    mode: EXTRANET_PRICING_MODES.AIR_TO_ANY,
    fromIcon: Plane,
    fromLabel: "Airport",
    toIcon: MapPin,
    toLabel: "Any",
  },
  {
    mode: EXTRANET_PRICING_MODES.AIR_TO_ZONE,
    fromIcon: Plane,
    fromLabel: "Airport",
    toIcon: MapPinned,
    toLabel: "Zone",
  },
  {
    mode: EXTRANET_PRICING_MODES.ANY_TO_ANY,
    fromIcon: MapPin,
    fromLabel: "Any",
    toIcon: MapPin,
    toLabel: "Any",
  },
  {
    mode: EXTRANET_PRICING_MODES.ANY_TO_ZONE,
    fromIcon: MapPin,
    fromLabel: "Any",
    toIcon: MapPinned,
    toLabel: "Zone",
  },
  {
    mode: EXTRANET_PRICING_MODES.ZONE_TO_ZONE,
    fromIcon: MapPinned,
    fromLabel: "Zone",
    toIcon: MapPinned,
    toLabel: "Zone",
  },
];

export function ModeSelection() {
  const dispatch = useDispatch();

  const currentSelectedCityId = useSelector(
    ({ pricing }) => pricing.currentSelectedCityId
  );

  const newZoneCreationForm = useSelector(
    ({ pricing }) => pricing.newZoneCreationForm
  );

  const mode = newZoneCreationForm.mode;

  const zones = useSelector(({ pricing }) => pricing.zones);
  const oneWayZones = Object.keys(zones).filter(
    (zoneId) =>
      zones[zoneId].bookingType === MODES[0] &&
      zones[zoneId].cityId === currentSelectedCityId
  );

  const onewayZoneListLength = oneWayZones.length;

  const disableFunction = (mode) => {
    return (
      // 2 Modes will be active if no prices given
      (onewayZoneListLength === 0 &&
        mode !== EXTRANET_PRICING_MODES.AIR_TO_ANY &&
        mode !== EXTRANET_PRICING_MODES.AIR_TO_ZONE) ||
      // All any modes will be deactivated if no air-to-any given
      (onewayZoneListLength !== 0 &&
        oneWayZones.filter(
          (zoneId) => zones[zoneId].mode === EXTRANET_PRICING_MODES.AIR_TO_ANY
        ).length === 0 &&
        mode !== EXTRANET_PRICING_MODES.AIR_TO_ZONE &&
        mode !== EXTRANET_PRICING_MODES.ZONE_TO_ZONE)
    );
  };

  const showMessage =
    mode.toLowerCase().includes("zone") &&
    oneWayZones.filter(
      (zoneId) => zones[zoneId].mode === EXTRANET_PRICING_MODES.AIR_TO_ANY
    ).length !== 0;

  const handleModeChange = (newMode) => {
    dispatch(setModeChange({ mode: newMode }));
  };

  React.useEffect(() => {
    dispatch(setModeChange({ mode: "" }));
  }, [currentSelectedCityId, onewayZoneListLength]);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-1 lg:grid-cols-2">
      {PRICING_MODES_CONFIG.map(
        (
          {
            mode: modeType,
            fromIcon: FromIcon,
            fromLabel,
            toIcon: ToIcon,
            toLabel,
          },
          index
        ) => (
          <Button
            type="button"
            disabled={disableFunction(modeType)}
            key={`${currentSelectedCityId}-${modeType}`}
            onClick={() => handleModeChange(modeType)}
            className={cn(
              "grid h-auto w-full grid-cols-[auto_auto_auto] hover:bg-[#181E29] gap-2 rounded-md bg-[#181E29] p-4 text-[#B3B3B3]  border border-transparent transition-transform hover:cursor-pointer hover:[@media(pointer:coarse)]:bg-[#B3B3B3] hover:[@media(pointer:fine)]:bg-[#181E29]",
              mode === modeType &&
                "scale-95 bg-[#181E29] shadow-blueGlow border border-[#7f9be8] hover:border-[#7f9be8]  hover:[@media(pointer:coarse)]:border-[#7f9be8]  hover:bg-[#181E29] text-[#7f9be8] font-thin duration-200",
              PRICING_MODES_CONFIG.length === index + 1 && "col-span-full"
            )}
          >
            <div>
              <FromIcon className="mx-auto size-3 xl:size-4" />
              <p className="text-center text-xs font-medium xl:text-sm">
                {fromLabel}
              </p>
            </div>

            <div className="h-7">
              <BiDirectionalArrowIcon className="mx-auto size-4 xl:size-5 " />
            </div>

            <div>
              <ToIcon className="mx-auto size-4 xl:size-5" />
              <p className="text-center text-xs font-medium xl:text-sm">
                {toLabel}
              </p>
            </div>
          </Button>
        )
      )}
      {showMessage && (
        <p className="text-xs text-yellow-500 leading-5 col-span-full flex gap-x-1 items-start">
          <TriangleAlert className="text-yellow-500 size-5" />
          Make sure the drop off is outside of the city or there will be
          duplications in price
        </p>
      )}
    </div>
  );
}
